import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { firstValueFrom, map } from 'rxjs';
import { environment } from '../../environment/environment';
import { SignalFormField } from '../core/functions/signal-form-field';
import { SignalInputComponent } from '../shared/components/signal-input/signal-input.component';

type ProviderLocation = {
  id: number;
  address: string;
  address2?: string;
  city: string;
  state: string;
  zip_code: string;
  phone?: string;
  fax?: string;
  active?: boolean | number | string;
};

type ProviderSummary = {
  id: number;
  first_name: string;
  last_name: string;
  locations: ProviderLocation;
};

type ClinicSummary = {
  id: number;
  clinic_name: string;
  clinic_website?: string;
  providers: ProviderSummary[];
};

type ClinicsSummaryResponse = {
  result: ClinicSummary[];
};

type ProviderDirectoryLocation = {
  id: number;
  name: string;
  phone: string;
  fax?: string;
  clinic_id: number;
  clinic_name: string;
  clinic_website?: string;
  location_id: number;
  address: string;
  address2?: string;
  city: string;
  state: string;
  zip_code: string;
  distance_miles?: number | null;
  active?: boolean | number | string;
  providers: Pick<ProviderSummary, 'id' | 'first_name' | 'last_name'>[];
};

type NearestLocationRequest = {
  userLatitude: number;
  userLongitude: number;
};

type NearestLocationResponseItem = {
  id?: number;
  location_id?: number;
  locationId?: number;
  clinic_location_id?: number;
  clinicLocationId?: number;
  distance_miles?: number | null;
  distanceMiles?: number | null;
  distance?: number | null;
  clinic_id?: number | string;
  clinicId?: number | string;
  active?: boolean | number | string;
  primary_location?: boolean | number | string;
  clinic_name?: string;
  name?: string;
  address?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  postal_code?: string;
  phone?: string;
  fax?: string;
};

type NearestLocationResponse =
  | NearestLocationResponseItem[]
  | {
      success?: boolean;
      locations?: NearestLocationResponseItem[];
      nearest_locations?: NearestLocationResponseItem[];
      nearestLocations?: NearestLocationResponseItem[];
      result?: NearestLocationResponseItem[] | { locations?: NearestLocationResponseItem[] };
      data?: NearestLocationResponseItem[] | { locations?: NearestLocationResponseItem[] };
      radius?: number;
      message?: string;
    };

@Component({
  selector: 'app-find-a-proivder',
  standalone: true,
  imports: [
    CommonModule,
    SignalInputComponent,
    ReactiveFormsModule,
    RouterLink
  ],
  templateUrl: './find-a-proivder.component.html',
  styleUrl: './find-a-proivder.component.scss'
})
export class FindAProivderComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.apiBase;

  private readonly clinicSummaries = signal<ClinicSummary[]>([]);
  private readonly allProviderData = signal<ProviderDirectoryLocation[]>([]);
  private readonly allNearestProviderData = signal<ProviderDirectoryLocation[]>([]);

  readonly radiusOptions: Array<number | 'all'> = [5, 10, 20, 30, 50, 'all'];
  selectedRadius = signal<number | 'all'>(50);
  visibleResultCount = signal(5);

  providerData = signal<ProviderDirectoryLocation[]>([]);
  loading = signal(false);
  distanceLoading = signal(false);
  usingCurrentLocation = signal(false);
  locationError = signal('');

  locationSearch = SignalFormField<string>('');

  showBeyondRadiusLink = computed(() =>
    this.usingCurrentLocation() &&
    !this.distanceLoading() &&
    this.selectedRadius() === 50 &&
    !this.locationSearch.value() &&
    this.providerData().length === 0 &&
    this.allNearestProviderData().length > 0
  );

  filterClinicData = computed(() => {
    const hasManualSearch = this.normalizeSearchTerm(this.locationSearch.value());

    const source = hasManualSearch
      ? this.allProviderData()
      : this.providerData();

    return this.filterClinicProviderData(source);
  });

  visibleClinicData = computed(() =>
    this.filterClinicData().slice(0, this.visibleResultCount())
  );

  hasMoreResults = computed(() =>
    this.visibleResultCount() < this.filterClinicData().length
  );

  async ngOnInit(): Promise<void> {
    try {
      this.loading.set(true);

      const clinicProviderData = await firstValueFrom(
        this.http.get<ClinicsSummaryResponse>(`${this.apiUrl}/clinics/summaries`).pipe(
          map((response) => response.result ?? [])
        )
      );

      this.clinicSummaries.set(clinicProviderData);

      const transformed = this.transformData(clinicProviderData);
      this.allProviderData.set(transformed);
      this.providerData.set(transformed);
    } catch (error) {
      console.error('Failed to load provider summaries', error);
      this.locationError.set('Unable to load provider locations right now. Please try again.');
    } finally {
      this.loading.set(false);
    }
  }

  onSearchInput(value: string): void {
    this.visibleResultCount.set(5);
    this.locationSearch.setValue(value);

    // Manual search should always search the full provider network,
    // even after the user previously searched by current location.
    if (this.normalizeSearchTerm(value)) {
      this.usingCurrentLocation.set(false);
      this.allNearestProviderData.set([]);
      this.providerData.set(this.clearDistanceValues(this.allProviderData()));
    }
  }

  findProviders(): void {
    this.visibleResultCount.set(5);
    this.locationError.set('');
    this.usingCurrentLocation.set(false);
    this.allNearestProviderData.set([]);
    this.providerData.set(this.clearDistanceValues(this.allProviderData()));
  }

  onRadiusChange(value: string): void {
    this.visibleResultCount.set(5);
    if (value === 'all') {
      this.selectedRadius.set('all');
    } else {
      const radius = Number(value);

      if (!Number.isFinite(radius)) {
        return;
      }

      this.selectedRadius.set(radius);
    }

    if (this.usingCurrentLocation() && this.allNearestProviderData().length) {
      this.applySelectedRadiusFilter();
    }
  }

  showNearestBeyondRadius(): void {
    this.visibleResultCount.set(5);
    this.locationError.set('');
    this.providerData.set(this.allNearestProviderData().slice(0, 5));
    this.usingCurrentLocation.set(true);
  }

  reset(): void {
    this.visibleResultCount.set(5);
    this.locationSearch.setValue('');
    this.locationError.set('');
    this.usingCurrentLocation.set(false);
    this.allNearestProviderData.set([]);
    this.providerData.set(this.clearDistanceValues(this.allProviderData()));
  }

  async useCurrentLocation(): Promise<void> {
    this.visibleResultCount.set(5);
    this.locationSearch.setValue('');
    this.locationError.set('');

    if (!('geolocation' in navigator)) {
      this.locationError.set('Location services are not supported by this browser.');
      return;
    }

    await this.loadNearestLocationsFromUserLocation();
  }

  async loadNearestLocationsFromUserLocation(): Promise<void> {
    this.visibleResultCount.set(5);

    try {
      this.distanceLoading.set(true);

      const position = await this.getCurrentPosition();

      const requestBody: NearestLocationRequest = {
        userLatitude: position.coords.latitude,
        userLongitude: position.coords.longitude
      };

      const response = await firstValueFrom(
        this.http.post<NearestLocationResponse>(
          `${this.apiUrl}/utility/nearest-locations`,
          requestBody
        )
      );

      const nearestLocations = this.extractNearestLocations(response);

      if (!nearestLocations.length) {
        this.providerData.set([]);
        this.allNearestProviderData.set([]);
        this.usingCurrentLocation.set(true);
        return;
      }

      const mergedNearestLocations = this.mergeNearestLocationsWithClinicSummaries(nearestLocations)
        .sort((a, b) => {
          if (a.distance_miles === null || a.distance_miles === undefined) return 1;
          if (b.distance_miles === null || b.distance_miles === undefined) return -1;
          return a.distance_miles - b.distance_miles;
        });

      this.allNearestProviderData.set(mergedNearestLocations);
      this.usingCurrentLocation.set(true);
      this.applySelectedRadiusFilter();
    } catch (error: any) {
      console.error('Failed to load nearest clinic locations', error);
      this.usingCurrentLocation.set(false);
      this.allNearestProviderData.set([]);
      this.providerData.set(this.clearDistanceValues(this.allProviderData()));
      this.locationError.set(this.getLocationErrorMessage(error));
    } finally {
      this.distanceLoading.set(false);
    }
  }


  showMoreResults(): void {
    this.visibleResultCount.update((count) => count + 5);
  }

  getSelectedRadiusLabel(): string {
    const radius = this.selectedRadius();
    return radius === 'all' ? 'All' : `${radius} miles`;
  }

  private applySelectedRadiusFilter(): void {
    const radius = this.selectedRadius();
    const nearestLocations = this.allNearestProviderData();

    if (radius === 'all') {
      this.providerData.set(nearestLocations);
      return;
    }

    this.providerData.set(
      nearestLocations.filter((location) => {
        if (location.distance_miles === null || location.distance_miles === undefined) {
          return false;
        }

        return location.distance_miles <= radius;
      })
    );
  }

  private getCurrentPosition(): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        resolve,
        reject,
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000
        }
      );
    });
  }

  private getLocationErrorMessage(error: any): string {
    if (error?.code === 1) {
      return 'Location permission was denied. You can still search by city, state, or zip code.';
    }

    if (error?.code === 2) {
      return 'Your location is unavailable. Please search by city, state, or zip code.';
    }

    if (error?.code === 3) {
      return 'Location request timed out. Please try again or search by city, state, or zip code.';
    }

    return 'Unable to get your current location. Please search by city, state, or zip code.';
  }

  private extractNearestLocations(response: NearestLocationResponse): NearestLocationResponseItem[] {
    if (Array.isArray(response)) {
      return response;
    }

    if (Array.isArray(response?.locations)) {
      return response.locations;
    }

    if (Array.isArray(response?.nearest_locations)) {
      return response.nearest_locations;
    }

    if (Array.isArray(response?.nearestLocations)) {
      return response.nearestLocations;
    }

    if (Array.isArray(response?.result)) {
      return response.result;
    }

    if (!Array.isArray(response?.result) && Array.isArray(response?.result?.locations)) {
      return response.result.locations;
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    if (!Array.isArray(response?.data) && Array.isArray(response?.data?.locations)) {
      return response.data.locations;
    }

    return [];
  }

  private getNearestLocationId(location: NearestLocationResponseItem): number | null {
    const id =
      location.location_id ??
      location.locationId ??
      location.clinic_location_id ??
      location.clinicLocationId ??
      location.id;

    const numericId = Number(id);

    return Number.isFinite(numericId) && numericId > 0 ? numericId : null;
  }

  private getNearestClinicId(location: NearestLocationResponseItem): number | null {
    const id = location.clinic_id ?? location.clinicId;
    const numericId = Number(id);

    return Number.isFinite(numericId) && numericId > 0 ? numericId : null;
  }

  private getNearestLocationDistance(location: NearestLocationResponseItem): number | null {
    const distance = location.distance_miles ?? location.distanceMiles ?? location.distance ?? null;

    if (distance === null || distance === undefined) {
      return null;
    }

    const numericDistance = Number(distance);
    return Number.isFinite(numericDistance) ? Math.round(numericDistance * 10) / 10 : null;
  }

  private filterClinicProviderData(
    clinicData: ProviderDirectoryLocation[]
  ): ProviderDirectoryLocation[] {
    const term = this.normalizeSearchTerm(this.locationSearch.value());

    if (!term) {
      return clinicData;
    }

    return clinicData.filter((clinic) => {
      const providerNames = clinic.providers
        .map(provider => `${provider.first_name} ${provider.last_name}`)
        .join(' ');

      const searchableText = this.normalizeSearchTerm(`
        ${clinic.clinic_name}
        ${clinic.name}
        ${clinic.address}
        ${clinic.address2 ?? ''}
        ${clinic.city}
        ${clinic.state}
        ${clinic.zip_code}
        ${clinic.phone}
        ${providerNames}
      `);

      return searchableText.includes(term);
    });
  }

  private normalizeSearchTerm(value: string | null | undefined): string {
    return String(value ?? '')
      .toLowerCase()
      .replace(/,/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

private transformData(data: ClinicSummary[]): ProviderDirectoryLocation[] {
  const result: ProviderDirectoryLocation[] = [];

  data.forEach((clinic: any) => {
    const locationMap: Record<number, ProviderDirectoryLocation> = {};

    clinic.providers?.forEach((provider: any) => {
      const providerLocations = Array.isArray(provider.locations)
        ? provider.locations
        : provider.locations
          ? [provider.locations]
          : [];

      providerLocations.forEach((loc: any) => {
        if (!loc?.id || loc.active === false || loc.active === 0 || loc.active === '0') {
          return;
        }

        const locationId = Number(loc.id);

        if (!locationMap[locationId]) {
          locationMap[locationId] = {
            id: locationId,
            name: clinic.clinic_name,
            phone: loc.phone ?? clinic.phone ?? '',
            fax: loc.fax ?? clinic.fax ?? '',
            clinic_id: Number(clinic.id ?? 0),
            clinic_name: clinic.clinic_name ?? 'Provider Location',
            clinic_website: clinic.clinic_website,
            location_id: locationId,
            address: loc.address ?? '',
            address2: loc.address2 ?? '',
            city: loc.city ?? '',
            state: loc.state ?? '',
            zip_code: loc.zip_code ?? loc.postal_code ?? '',
            distance_miles: null,
            active: loc.active ?? true,
            providers: []
          };
        }

        locationMap[locationId].providers.push({
          id: Number(provider.id ?? provider.provider_id ?? 0),
          first_name: provider.first_name ?? '',
          last_name: provider.last_name ?? ''
        });
      });
    });

    result.push(...Object.values(locationMap));
  });

  return result.sort((a, b) =>
    (a.clinic_name || '').localeCompare(b.clinic_name || '') ||
    (a.city || '').localeCompare(b.city || '') ||
    (a.address || '').localeCompare(b.address || '')
  );
}

  private mergeNearestLocationsWithClinicSummaries(
    nearestLocations: NearestLocationResponseItem[]
  ): ProviderDirectoryLocation[] {
    const summaryLocations = this.transformData(this.clinicSummaries());
    const clinicById = new Map<number, ClinicSummary>();

    this.clinicSummaries().forEach((clinic) => {
      clinicById.set(Number(clinic.id), clinic);
    });

    return nearestLocations
      .map((location) => {
        const locationId = this.getNearestLocationId(location);
        const clinicId = this.getNearestClinicId(location);
        
        
        const distance = this.getNearestLocationDistance(location);

        const matchedSummaryLocation = summaryLocations.find((summaryLocation) => {
          const sameLocationId = locationId !== null && Number(summaryLocation.location_id) === Number(locationId);
          const sameClinicId = clinicId !== null && Number(summaryLocation.clinic_id) === Number(clinicId);
          const sameAddress = this.isSameAddress(summaryLocation, location);

          return sameLocationId || (sameClinicId && sameAddress);
        });

        if (matchedSummaryLocation) {
          return {
            ...matchedSummaryLocation,
            id: locationId ?? matchedSummaryLocation.id,
            location_id: locationId ?? matchedSummaryLocation.location_id,
            clinic_id: clinicId ?? matchedSummaryLocation?.clinic_id ?? 0,
           
            clinic_name: matchedSummaryLocation.clinic_name,
            name: matchedSummaryLocation.name,
            clinic_website: matchedSummaryLocation.clinic_website,
            address: location.address ?? matchedSummaryLocation.address,
            address2: location.address2 ?? matchedSummaryLocation.address2,
            city: location.city ?? matchedSummaryLocation.city,
            state: location.state ?? matchedSummaryLocation.state,
            zip_code: location.zip_code ?? location.postal_code ?? matchedSummaryLocation.zip_code,
            phone: location.phone ?? matchedSummaryLocation.phone,
            fax: location.fax ?? matchedSummaryLocation.fax,
            distance_miles: distance
          };
        }

        const clinic = clinicId !== null ? clinicById.get(clinicId) : undefined;

        if (clinic) {
          const clinicLocations = this.transformData([clinic]);
          const bestClinicLocation =
            clinicLocations.find((summaryLocation) => this.isSameAddress(summaryLocation, location)) ??
            clinicLocations.find((summaryLocation) => Number(summaryLocation.location_id) === Number(locationId)) ??
            clinicLocations[0];

          if (bestClinicLocation) {
            return {
              ...bestClinicLocation,
              id: locationId ?? bestClinicLocation.id,
              location_id: locationId ?? bestClinicLocation.location_id,
              clinic_id: clinicId ?? clinic.id ?? 0,
              clinic_name: clinic.clinic_name,
              name: clinic.clinic_name,
              clinic_website: clinic.clinic_website,
              address: location.address ?? bestClinicLocation.address,
              address2: location.address2 ?? bestClinicLocation.address2,
              city: location.city ?? bestClinicLocation.city,
              state: location.state ?? bestClinicLocation.state,
              zip_code: location.zip_code ?? location.postal_code ?? bestClinicLocation.zip_code,
              phone: location.phone ?? bestClinicLocation.phone,
              fax: location.fax ?? bestClinicLocation.fax,
              distance_miles: distance,
              providers: bestClinicLocation.providers.length
                ? bestClinicLocation.providers
                : clinic.providers.map((provider) => ({
                    id: provider.id,
                    first_name: provider.first_name,
                    last_name: provider.last_name
                  }))
            };
          }
        }

        return this.buildDirectoryLocationFromNearestLocation(location, clinic, locationId, clinicId, distance);
      })
      .filter((location) => location.active !== false && location.active !== 0 && location.active !== '0')
      .filter((location, index, list) =>
        index === list.findIndex((existing) => existing.location_id === location.location_id)
      );
  }

  private buildDirectoryLocationFromNearestLocation(
    location: NearestLocationResponseItem,
    clinic: ClinicSummary | undefined,
    locationId: number | null,
    clinicId: number | null,
    distance: number | null
  ): ProviderDirectoryLocation {
    const providers = (clinic?.providers ?? [])
      .filter((provider) => {
        if (!locationId) {
          return true;
        }

        return Number(provider.locations?.id ?? 0) === Number(locationId) || Number(provider.locations?.id ?? 0) === 0;
      })
      .map((provider) => ({
        id: provider.id,
        first_name: provider.first_name,
        last_name: provider.last_name
      }));

    return {
      id: locationId ?? Number(location.id ?? 0),
      name: clinic?.clinic_name ?? location.clinic_name ?? location.name ?? 'Provider Location',
      phone: location.phone ?? '',
      fax: location.fax ?? '',
      clinic_id: clinicId ?? Number(location.clinic_id ?? 0),
      clinic_name: clinic?.clinic_name ?? location.clinic_name ?? location.name ?? 'Provider Location',
      clinic_website: clinic?.clinic_website,
      location_id: locationId ?? Number(location.id ?? 0),
      address: location.address ?? '',
      address2: location.address2 ?? '',
      city: location.city ?? '',
      state: location.state ?? '',
      zip_code: location.zip_code ?? location.postal_code ?? '',
      distance_miles: distance,
      active: location.active ?? true,
      providers
    };
  }

  private isSameAddress(
    provider: ProviderDirectoryLocation,
    location: NearestLocationResponseItem
  ): boolean {
    const providerAddressKey = this.buildAddressKey(
      provider.address,
      provider.city,
      provider.state,
      provider.zip_code
    );

    const locationAddressKey = this.buildAddressKey(
      location.address ?? '',
      location.city ?? '',
      location.state ?? '',
      location.zip_code ?? location.postal_code ?? ''
    );

    return !!providerAddressKey && providerAddressKey === locationAddressKey;
  }

  private clearDistanceValues(locations: ProviderDirectoryLocation[]): ProviderDirectoryLocation[] {
    return locations.map((location) => ({
      ...location,
      distance_miles: null
    }));
  }

  private buildAddressKey(address: string, city: string, state: string, zipCode: string): string {
    return this.normalizeText(`${address} ${city} ${state} ${zipCode}`);
  }

  private normalizeText(value: string): string {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .trim();
  }
}
